import { useEffect, useState } from 'react';
import type { ComplianceControl, ComplianceEvidence } from '@hospasense/contracts';
import { COMPLIANCE_FRAMEWORKS } from '@hospasense/contracts';
import {
  addControlEvidence,
  downloadComplianceExport,
  fetchComplianceControls,
  fetchControlEvidence,
  grantControlException,
  recordControlTest,
} from '../lib/queries-s5.js';
import { useAuth } from '../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Select,
  Textarea,
  useToast,
} from '../components/ui/index.js';

const FRAMEWORK_LABEL: Record<string, string> = {
  dpdp_act_2023: 'DPDP Act 2023',
  iso_27001: 'ISO 27001',
  internal_policy: 'Internal policy',
};

function Flag({ on, label }: { on: boolean; label: string }) {
  return (
    <Badge tone={on ? 'success' : 'neutral'}>
      <span aria-hidden="true">{on ? '✓' : '○'}</span> {label}
    </Badge>
  );
}

export function CompliancePage() {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canManage = hasPermission('compliance.manage');
  const canExport = hasPermission('compliance.evidence.export');

  const [controls, setControls] = useState<ComplianceControl[]>([]);
  const [framework, setFramework] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [evidence, setEvidence] = useState<Record<string, ComplianceEvidence[]>>({});

  const [modal, setModal] = useState<{
    kind: 'evidence' | 'test' | 'exception';
    control: ComplianceControl;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    reference: 'evidence://synthetic/',
    expiresAt: '',
    outcome: 'effective' as 'effective' | 'ineffective',
    note: '',
    reason: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      setControls(await fetchComplianceControls(framework || undefined));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, [framework]);

  const toggleEvidence = async (control: ComplianceControl) => {
    if (expanded === control.id) {
      setExpanded(null);
      return;
    }
    setExpanded(control.id);
    if (!evidence[control.id]) {
      try {
        const items = await fetchControlEvidence(control.id);
        setEvidence((prev) => ({ ...prev, [control.id]: items }));
      } catch (err) {
        toast.error((err as Error).message);
      }
    }
  };

  const submitModal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modal) return;
    setSaving(true);
    try {
      if (modal.kind === 'evidence') {
        await addControlEvidence(modal.control.id, {
          title: form.title,
          reference: form.reference,
          ...(form.expiresAt ? { expiresAt: new Date(form.expiresAt).toISOString() } : {}),
        });
        toast.success('Evidence reference attached');
      } else if (modal.kind === 'test') {
        await recordControlTest(modal.control.id, {
          outcome: form.outcome,
          note: form.note,
          expectedVersion: modal.control.version,
        });
        toast.success('Effectiveness test recorded (append-only)');
      } else {
        await grantControlException(modal.control.id, {
          reason: form.reason,
          expiresAt: new Date(form.expiresAt).toISOString(),
          expectedVersion: modal.control.version,
        });
        toast.success('Time-bound exception granted');
      }
      setModal(null);
      setEvidence({});
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <PageHeader
        title="Compliance Controls"
        subtitle="Four independent claims per control — configured, evidenced, tested, interpreted. A green row is exactly those four flags; nothing here claims a certification. Legal interpretation is counsel-approved configuration text."
        actions={
          canExport && (
            <Button
              variant="secondary"
              data-testid="compliance-export"
              onClick={() =>
                void downloadComplianceExport()
                  .then(() => toast.success('Export downloaded (audited)'))
                  .catch((err: Error) => toast.error(err.message))
              }
            >
              Export evidence CSV
            </Button>
          )
        }
      />
      <div className="max-w-xs">
        <Select label="Framework" value={framework} onChange={(e) => setFramework(e.target.value)}>
          <option value="">All frameworks</option>
          {COMPLIANCE_FRAMEWORKS.map((f) => (
            <option key={f} value={f}>
              {FRAMEWORK_LABEL[f]}
            </option>
          ))}
        </Select>
      </div>
      {error && <ErrorState description={error} onRetry={() => void load()} />}
      {loading ? (
        <LoadingState label="Loading control library…" />
      ) : (
        <div className="space-y-3">
          {controls.map((c) => (
            <Card key={c.id} data-testid={`control-${c.controlKey}`}>
              <CardBody>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono text-xs text-text-muted">
                      {c.controlKey} · {FRAMEWORK_LABEL[c.framework]}
                    </p>
                    <h2 className="text-sm font-semibold text-text-primary">{c.title}</h2>
                    <p className="mt-1 text-xs text-text-secondary">{c.obligation}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Flag on={c.configured} label="configured" />
                    <Flag on={c.evidenced} label="evidenced" />
                    <Flag on={c.tested} label="tested" />
                    <Flag on={c.interpreted} label="interpreted" />
                  </div>
                </div>
                <dl className="mt-3 grid grid-cols-1 gap-2 border-t border-border pt-3 text-xs sm:grid-cols-3">
                  <div>
                    <dt className="font-semibold uppercase tracking-wide text-text-muted">Owner</dt>
                    <dd className="text-text-secondary">{c.ownerDisplayName ?? 'unassigned'}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold uppercase tracking-wide text-text-muted">
                      Test cadence
                    </dt>
                    <dd className="text-text-secondary">
                      {c.testFrequency}
                      {c.lastTestedAt
                        ? ` · last ${new Date(c.lastTestedAt).toLocaleDateString()} (${c.lastTestOutcome})`
                        : ' · never tested'}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-semibold uppercase tracking-wide text-text-muted">
                      Exception
                    </dt>
                    <dd className="text-text-secondary">
                      {c.exceptionReason
                        ? `${c.exceptionReason} (until ${c.exceptionExpiresAt ? new Date(c.exceptionExpiresAt).toLocaleDateString() : '—'})`
                        : 'none'}
                    </dd>
                  </div>
                </dl>
                {c.counselInterpretation && (
                  <p className="mt-2 rounded-md bg-bg-subtle px-3 py-2 text-xs text-text-secondary">
                    <span className="font-semibold">Counsel interpretation</span>
                    {c.counselApprovedAt &&
                      ` (approved ${new Date(c.counselApprovedAt).toLocaleDateString()})`}
                    : {c.counselInterpretation}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3">
                  <Button variant="ghost" size="sm" onClick={() => void toggleEvidence(c)}>
                    {expanded === c.id ? 'Hide evidence' : 'Evidence'}
                  </Button>
                  {canManage && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setForm((f) => ({ ...f, title: '', reference: 'evidence://synthetic/' }));
                          setModal({ kind: 'evidence', control: c });
                        }}
                      >
                        Attach evidence
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setForm((f) => ({ ...f, note: '', outcome: 'effective' }));
                          setModal({ kind: 'test', control: c });
                        }}
                      >
                        Record test
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setForm((f) => ({ ...f, reason: '', expiresAt: '' }));
                          setModal({ kind: 'exception', control: c });
                        }}
                      >
                        Grant exception
                      </Button>
                    </>
                  )}
                </div>
                {expanded === c.id && (
                  <ul className="mt-2 space-y-1 text-xs">
                    {(evidence[c.id] ?? []).map((e) => (
                      <li key={e.id} className="flex flex-wrap items-center gap-2">
                        <Badge
                          tone={
                            e.status === 'collected'
                              ? 'success'
                              : e.status === 'expired'
                                ? 'warning'
                                : 'neutral'
                          }
                        >
                          {e.status}
                        </Badge>
                        <span className="font-medium text-text-primary">{e.title}</span>
                        <span className="font-mono text-text-muted">{e.reference}</span>
                        {e.expiresAt && (
                          <span className="text-text-muted">
                            expires {new Date(e.expiresAt).toLocaleDateString()}
                          </span>
                        )}
                      </li>
                    ))}
                    {(evidence[c.id] ?? []).length === 0 && (
                      <li className="text-text-muted">No evidence recorded.</li>
                    )}
                  </ul>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={modal !== null}
        title={
          modal?.kind === 'evidence'
            ? `Attach evidence — ${modal.control.controlKey}`
            : modal?.kind === 'test'
              ? `Record effectiveness test — ${modal.control.controlKey}`
              : `Grant exception — ${modal?.control.controlKey ?? ''}`
        }
        onClose={() => setModal(null)}
      >
        <form onSubmit={(e) => void submitModal(e)} className="space-y-3">
          {modal?.kind === 'evidence' && (
            <>
              <p className="text-xs text-text-muted">
                Reference or link only — evidence content never enters Control.
              </p>
              <Input
                label="Title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                minLength={3}
                required
              />
              <Input
                label="Reference"
                value={form.reference}
                onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
                minLength={3}
                required
              />
              <Input
                label="Expires (optional)"
                type="date"
                value={form.expiresAt}
                onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
              />
            </>
          )}
          {modal?.kind === 'test' && (
            <>
              <Select
                label="Outcome"
                value={form.outcome}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    outcome: e.target.value as 'effective' | 'ineffective',
                  }))
                }
              >
                <option value="effective">Effective</option>
                <option value="ineffective">Ineffective</option>
              </Select>
              <Textarea
                label="Note (min 10 characters — appended to the immutable test history)"
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                minLength={10}
                required
                rows={3}
              />
            </>
          )}
          {modal?.kind === 'exception' && (
            <>
              <Textarea
                label="Reason (min 10 characters, recorded and audited)"
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                minLength={10}
                required
                rows={3}
              />
              <Input
                label="Exception expires"
                type="date"
                value={form.expiresAt}
                onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
                required
              />
            </>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setModal(null)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={saving}>
              Save
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
